import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Button,
  Tooltip,
  MenuItem,
  FormControl,
  OutlinedInput,
  Select,
  Checkbox,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import { createPortal } from 'react-dom';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import SearchIcon from '@mui/icons-material/Search';
import Info from 'components/utils/Alert/Info';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { ShopContext } from 'Context/ShopContext';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import { AlertMessagesContext } from 'react-alert-messages';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import { saveAs } from 'file-saver';
import CsvHelper from 'helpers/CsvHelper';
import SyncIcon from '@mui/icons-material/Sync';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import PrintIcon from '@mui/icons-material/Print';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import LinkIcon from '@mui/icons-material/Link';
import { useHistory } from 'react-router-dom';
import { ROUTES, SelectFilterStyle, PRINT_TYPES } from 'const';
import { useReportPrint } from 'hooks/useReportPrint';
import PrintDocument from 'components/utils/PrintDocument/index';
import { useTranslation } from 'react-i18next';
import CustomerService from 'services/CustomerService';

function CustomerPaymentReport() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');

  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const history = useHistory();

  const [filterFromDate, setFilterFromDate] = useState(new Date());
  const [filterToDate, setFilterToDate] = useState(new Date());
  const [filterGroups, setFilterGroups] = useState([]);

  const [customerGroups, setCustomerGroups] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [items, setItems] = useState([]);
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
    filterGroups,
  });
  const { reportToPrint, handlePrint: handlePrintReportHook } = useReportPrint(shop, tCommon, tReport, DateTimeHelpers);

  const CSV_COLUMNS = {
    customer: 'Customer',
    customer_group: 'Group',
    customer_tax_id: 'Tax ID',
    customer_mobile: 'Mobile',
    nc_total: 'NC Total',
  };

  const toCsvBtnPressed = async () => {
    const csv = CsvHelper.getString(items, CSV_COLUMNS);
    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `NC-CUSTOMER-REPORT-${dateTime}.csv`);

    postAlertMessage({
      text: tReport('Exported to excel successfully'),
      type: 'success',
    });
  };

  const _getPaymentsFiltered = (items, searchKey) => {
    if (!searchKey) {
      return items;
    }

    const searchKeySmallCase = searchKey.toLowerCase();
    return (items || []).filter((item) => item?.customer.toLowerCase().includes(searchKeySmallCase));
  };

  const _getPaymentReportSummary = (items) => {
    let totalNc = 0;

    items.forEach((item) => {
      totalNc += parseFloat(item.nc_total) || 0;
    });

    return {
      totalNc: ShopsHelper.getAmountFormatted(shop, totalNc),
    };
  };

  const redirectToCustomerDetails = (item) => {
    history.push(ROUTES.CUSTOMER_DETAILS.replace(':customerId', item.customer_id));
  };

  const headerData = [
    {
      label: tCommon('Customer'),
      id: 'customer',
      type: 'callback',
      viewRender: (item) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span>{item?.customer ?? '-'}</span>
          {item?.customerId && (
            <Tooltip title={tCommon('View Customer')}>
              <LinkIcon
                style={{ cursor: 'pointer', marginLeft: 4 }}
                onClick={() => redirectToCustomerDetails(item.customer_id)}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      label: tCommon('Group'),
      id: 'customer_group',
      type: 'text',
    },
    {
      label: tCommon('Tax ID'),
      id: 'customer_tax_id',
      type: 'callback',
      viewRender: (item) => item.customer_tax_id || '-',
    },
    {
      label: tCommon('Mobile'),
      id: 'customer_mobile',
      type: 'callback',
      viewRender: (item) => item.customer_mobile || '-',
    },
    {
      label: tCommon('Amount'),
      id: 'nc_total',
      type: 'floatAmount',
    },
  ];

  const getCustomerGroups = async () => {
    const res = await CustomerService.getCustomerGroups();
    setCustomerGroups(res);
  };

  const generatePaymentHistory = () => {
    const { startDate, endDate } = dynamicDateRange('NC_CUSTOMER_SUMMARY');

    setFilterFromDate(startDate);
    setFilterToDate(endDate);

    getPayments({
      from_date: DateTimeHelpers.getDateYMD(startDate),
      to_date: DateTimeHelpers.getDateYMD(endDate),
      group_ids: filterGroups,
    });

    setIsShowGenerateButton(false);
    setPrevFilterData({
      filterFromDate: startDate,
      filterToDate: endDate,
      filterGroups,
    });
  };

  const getPayments = useCallback(
    async (customDateParams) => {
      setLoadingIndicator(true);
      try {
        const data = customDateParams;

        const res = await CustomerService.getCustomerNcReport(data);
        setItems(res.data);
      } catch (err) {
        postAlertMessage({ text: err.message, type: 'failed' });
      }
      setLoadingIndicator(false);
    },
    [postAlertMessage]
  );

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return (
      filterFromDate !== prevFilterData.filterFromDate ||
      filterToDate !== prevFilterData.filterToDate ||
      filterGroups !== prevFilterData.filterGroups
    );
  }, [
    filterFromDate,
    filterToDate,
    prevFilterData.filterFromDate,
    prevFilterData.filterToDate,
    filterGroups,
    prevFilterData.filterGroups,
  ]);

  useEffect(() => {
    getCustomerGroups();
    generatePaymentHistory();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters, isShowGenerateButton]);

  const handlePrintReport = () => {
    handlePrintReportHook(tReport('Customer NC Report'), filterFromDate, filterToDate, headerData, itemsToDisplay, {
      [tCommon('Total')]: `${shop?.currency || ''} ${summary.totalNc}`,
    });
  };

  const itemsToDisplay = _getPaymentsFiltered(items, searchText);
  const summary = _getPaymentReportSummary(items);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <span className={styles.title}>
          {tCommon('Reports')}
          <span className={styles.menuTitle}>{tCommon('Generation')}</span>
        </span>
        <div style={{ justifyContent: 'flex-end', marginRight: '10px' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>Print Report</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={handlePrintReport}
          >
            <PrintIcon className={styles.actionBtnIcon} />
            {tCommon('Print')}
          </Button>
        </div>
        <div style={{ justifyContent: 'flex-end' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>Export As</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a', marginRight: 5 }}
            onClick={toCsvBtnPressed}
          >
            <ImportExportIcon className={styles.actionBtnIcon} />
            Csv
          </Button>
        </div>
      </div>
      <div className={styles.changeable}>
        <div className={styles.filterSec}>
          <div className={styles.headTitle}>
            <h2 className={styles.subTitle}>
              {tReport('Customer NC Report from {{from}} to {{to}}', {
                from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                to: DateTimeHelpers.convertDateToDMY(filterToDate),
              })}
            </h2>
          </div>
          <div className={styles.filerInputSec}>
            <div className={styles.searchSec}>
              <input
                type="text"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                }}
                className={styles.searchInput}
                placeholder={tCommon('Search purchase')}
              />
              <Tooltip title="Search" placement="bottom">
                <SearchIcon className={styles.searchIcon} />
              </Tooltip>
            </div>
          </div>
        </div>
        <div className={styles.actionButtons}>
          <div className={styles.filterDiv}>
            <DateTimeRange
              setFilterFromDate={setFilterFromDate}
              setFilterToDate={setFilterToDate}
              storageKey="NC_CUSTOMER_SUMMARY"
            />
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Customer Groups')}</label>
            </div>
            <div>
              <FormControl>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  multiple
                  displayEmpty
                  value={filterGroups}
                  onChange={(e) => setFilterGroups(e.target.value)}
                  input={<OutlinedInput />}
                  style={{ width: 135, height: 40 }}
                  renderValue={(filterGroups) => {
                    if (filterGroups.length === 0) return <span>{tCommon('All')}</span>;
                    const selectedNames = filterGroups.map((id) => customerGroups.find((cg) => cg.id === id).name);
                    return selectedNames.join(', ');
                  }}
                  MenuProps={SelectFilterStyle}
                >
                  {customerGroups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      <Checkbox checked={filterGroups.includes(group.id)} color="primary" />
                      <ListItemText primary={group.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Load Purchases')}</label>
            </div>
            <div>
              <Button
                variant="contained"
                color="primary"
                size="small"
                className={styles.actionBtn}
                style={{ backgroundColor: '#00a65a' }}
                onClick={generatePaymentHistory}
                disabled={isShowGenerateButton === false || loadingIndicator}
              >
                {loadingIndicator ? (
                  <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                ) : (
                  <SyncIcon className={styles.actionBtnIcon} />
                )}
                {tCommon('Generate')}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {isShowGenerateButton === null ? (
        <Info
          title={tReport('Set filters and click generate button')}
          content={tReport(
            'Reports are generated based on the filters. Please set filters and click generate button to generate the report.'
          )}
        />
      ) : (
        items &&
        (itemsToDisplay && itemsToDisplay.length ? (
          <div className={styles.tableContainer}>
            <Grayout open={isShowGenerateButton} />
            <DataTable
              columns={headerData}
              rows={itemsToDisplay}
              summary={{
                customer: tCommon('Total'),
                nc_total: `${shop?.currency || ''} ${summary.totalNc}`,
              }}
            />
          </div>
        ) : (
          !loadingIndicator && (
            <Info
              title={tCommon('No payment data to list')}
              content={tCommon("You haven't any payment to list with given properties")}
            />
          )
        ))
      )}
      {reportToPrint &&
        createPortal(<PrintDocument type={PRINT_TYPES.REPORT} data={reportToPrint} shop={shop} />, document.body)}
    </div>
  );
}

export default withConsoleBase(CustomerPaymentReport);
